/**
 * Espacement entre containers proposal → marge basse section web (responsive rem).
 */

import {
  computeSectionMarginBottomCss,
  proposalPxToSpacingCss,
  proposalValueToPx,
} from './proposalUnits';

function spacingSizeMap(configuration = {}) {
  const map = {};
  (configuration.sizeSpaceContainer || []).forEach((entry) => {
    if (entry?.id != null) map[entry.id] = Number(entry.value) || 0;
  });
  return map;
}

/**
 * Le saut de page reste une box d'espacement bien qu'il ne pèse plus rien : un
 * container `[espace, saut de page]` doit continuer d'être vu comme « 100 %
 * espacement », sinon les pixels de l'espace se perdent avec lui.
 */
export function isSpacingBox(box) {
  return box?.type === 'space' || box?.type === 'line_break';
}

export function getSpacingBoxHeightPx(box, configuration = {}) {
  if (!box) return 0;
  if (box.type === 'space') {
    const sizes = spacingSizeMap(configuration);
    const sizeName = box.content?.sizeName
      || configuration.defaultSizeSpaceContainer
      || 'small';
    const fallback = sizes.small ?? sizes.medium ?? 15;
    return proposalValueToPx(sizes[sizeName] ?? fallback, 'sizeSpaceContainer');
  }
  // Un saut de page n'existe pas sur un site : il ne s'y affiche pas, et il ne
  // doit pas non plus y laisser le trou de sa hauteur.
  return 0;
}

/**
 * Colonnes portant réellement des boxes, sous-colonnes des lignes imbriquées
 * comprises (`docs/nested-rows.md`), dans l'ordre du document.
 *
 * Sans `rowContainers`, rend exactement `container.columnsIds` : le
 * comportement d'avant les lignes est intact.
 */
export function leafColumnIdsOfContainer(container, { columns = [], rowContainers = [] } = {}) {
  const columnById = new Map(columns.map((c) => [c.id, c]));
  const rowById = new Map(rowContainers.map((r) => [r.id, r]));

  const walk = (columnId) => {
    const rowIds = columnById.get(columnId)?.rowContainerIds ?? [];
    if (!rowIds.length) return [columnId];
    // La colonne porteuse reste dans la liste : `mutateSetColumnRows` ne
    // remonte que la première box dans la sous-colonne créée, les suivantes
    // restent attachées ici et comptent quand même.
    return [columnId, ...rowIds.flatMap((rowId) => (
      (rowById.get(rowId)?.columnsIds ?? []).flatMap(walk)
    ))];
  };

  return (container?.columnsIds || []).flatMap(walk);
}

function boxesInContainer(container, allBoxes, layout) {
  const colSet = new Set(leafColumnIdsOfContainer(container, layout));
  return (allBoxes || [])
    .filter((b) => colSet.has(b.columnId) && !b.hideField)
    .sort((a, b) => (a.top ?? 0) - (b.top ?? 0));
}

/** Container ne contient que des blocs espace / saut de ligne. */
export function isSpacingOnlyContainer(container, allBoxes, layout) {
  const boxes = boxesInContainer(container, allBoxes, layout);
  return boxes.length > 0 && boxes.every(isSpacingBox);
}

/** Espacement en fin de container (blocs espace / line_break trailing par colonne). */
export function computeContainerTrailingSpacingPx(
  container,
  allBoxes,
  configuration = {},
  layout,
) {
  let maxTrailing = 0;
  leafColumnIdsOfContainer(container, layout).forEach((colId) => {
    const colBoxes = (allBoxes || [])
      .filter((b) => b.columnId === colId && !b.hideField)
      .sort((a, b) => (a.top ?? 0) - (b.top ?? 0));

    let trailing = 0;
    for (let i = colBoxes.length - 1; i >= 0; i -= 1) {
      if (!isSpacingBox(colBoxes[i])) break;
      trailing += getSpacingBoxHeightPx(colBoxes[i], configuration);
    }
    maxTrailing = Math.max(maxTrailing, trailing);
  });
  return maxTrailing;
}

/** Somme de tous les blocs espace / line_break d'un container. */
export function computeContainerAllSpacingPx(
  container,
  allBoxes,
  configuration = {},
  layout,
) {
  return boxesInContainer(container, allBoxes, layout)
    .filter(isSpacingBox)
    .reduce((sum, box) => sum + getSpacingBoxHeightPx(box, configuration), 0);
}

export { computeSectionMarginBottomCss, proposalPxToSpacingCss };
